import type { SupWeb3WithdrawSingleVault, SupWithdrawableToken } from "@/types";
import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react";

import UiTooltip from "@/components/Ui/Tooltip";

import { formatCurrencyCompact } from "@/utils";
import { FixedNumber } from "@ethersproject/bignumber";
import config from "@/../config/dapp.config";

import { SupSlider, SupText } from "@repo/core-ui";
import { twMerge } from "tailwind-merge";

interface Web3WithdrawTargetAmountProps {
  selectedBalance: SupWithdrawableToken;
  vaultList: SupWeb3WithdrawSingleVault[];
  setVaultList: Dispatch<SetStateAction<SupWeb3WithdrawSingleVault[]>>;
}

export default function Web3WithdrawTargetAmount({ setVaultList, vaultList, selectedBalance }: Web3WithdrawTargetAmountProps) {
  const { value, totalFiatAmount, totalFiatBalance } = useMemo(() => {
    const totalFiatAmount = vaultList.reduce((acc, vault) => acc + parseFloat(`${vault.amountInFiat}`), 0);
    const totalAmount = vaultList.reduce((acc, vault) => acc.addUnsafe(vault.amount ?? FixedNumber.from("0")), FixedNumber.from("0"));
    const totalFiatBalance = vaultList.reduce((acc, vault) => acc + parseFloat(`${vault.balanceInFiat}`), 0);
    const totalBalance = vaultList.reduce((acc, vault) => acc.addUnsafe(vault.balance ?? FixedNumber.from("0")), FixedNumber.from("0"));

    const value = totalFiatBalance === 0 ? (totalBalance.toUnsafeFloat() / totalAmount.toUnsafeFloat()) * 100 : (totalFiatAmount / totalFiatBalance) * 100;
    return {
      value,
      totalFiatAmount,
      totalAmount,
      totalFiatBalance,
    };
    // eslint-disable-next-line
  }, [selectedBalance, vaultList]);

  let handlePercentChange: (value: number | number[]) => void;
  handlePercentChange = useCallback(
    (value: number | number[]) => {
      const percent = FixedNumber.from(`${(value as number) / 100}`);
      setVaultList(currentVaultList =>
        currentVaultList.map(
          vault =>
            ({
              ...vault,
              amount: (vault.balance ?? FixedNumber.from("0")).mulUnsafe(FixedNumber.from(`${percent}`)),
              amountInFiat: (vault.balanceInFiat ?? FixedNumber.from("0")).mulUnsafe(FixedNumber.from(`${percent}`)),
            }) as SupWeb3WithdrawSingleVault
        )
      );
    },
    [setVaultList]
  );

  const displayFiat = formatCurrencyCompact(totalFiatAmount, config.decimalsRoundingLimit, config.decimalsRoundingLimit, "currency", "standard");

  return (
    <UiTooltip content="For the vault(s) in your bag you should modify amount per vault" enable={totalFiatBalance === 0} arrowPlacement="bottom-start">
      <div className="mb-6 inline-flex w-full flex-col items-start justify-start gap-1">
        <SupSlider
          disabled={totalFiatBalance === 0}
          grabberType="value"
          className="w-full border-t-0"
          value={value}
          marks={25}
          formatDisplayValue={value => (typeof value === "undefined" ? undefined : `${value}%`)}
          onAfterChange={handlePercentChange}
          pushable
        />

        <div className="inline-flex h-4 w-full items-center justify-between gap-1">
          <div className={twMerge("flex h-4 shrink grow basis-0 items-center justify-end gap-1", totalFiatBalance === 0 && "invisible")}>
            <SupText variant="caption" className="leading-[16px]">
              WITHDRAWING TODAY
            </SupText>
            <SupText variant="button" weight="regular">
              {displayFiat}
            </SupText>
          </div>
        </div>
      </div>
    </UiTooltip>
  );
}
